import { Badge } from '@/components/ui/badge';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import {
    FormSection,
    MasterDataForm,
} from '../components/master-data/MasterDataForm';
import {
    FilterConfig,
    MasterDataTable,
    TableColumn,
} from '../components/master-data/MasterDataTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { License } from '../types/license';

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const LicenseManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingLicense, setEditingLicense] = useState<License | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side pagination, filtering, and searching
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>(
    {}
  );
  const [sortConfig, setSortConfig] = useState<{
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }>({});

  // Fetch licenses with server-side filtering and pagination
  const {
    data: licensesResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      'licenses',
      currentPage,
      pageSize,
      searchTerm,
      currentFilters,
      sortConfig,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(
        `/api/master-data/license?${params.toString()}`
      );
      const result = response.data.data || {};

      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1,
        },
      };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const licenses = licensesResponse?.data || [];
  const pagination = licensesResponse?.pagination;

  // Create mutation
  const createLicenseMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/license', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'license'],
      });
      toast({
        title: 'Success',
        description: 'License created successfully',
        variant: 'default',
      });
      setIsFormOpen(false);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to create license';
      const errors = error.response?.data?.error?.details || {};

      setServerErrors(errors);

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateLicenseMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await axios.put(
        `/api/master-data/license/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'license'],
      });
      toast({
        title: 'Success',
        description: 'License updated successfully',
        variant: 'default',
      });
      setIsFormOpen(false);
      setEditingLicense(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to update license';
      const errors = error.response?.data?.error?.details || {};

      setServerErrors(errors);

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Delete mutation (hard delete)
  const deleteLicenseMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(
        `/api/master-data/license/${id}?soft=false`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'license'],
      });
      toast({
        title: 'Success',
        description: 'License deleted successfully',
        variant: 'default',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description:
          error.response?.data?.error?.message || 'Failed to delete license',
        variant: 'destructive',
      });
    },
  });

  // Table columns configuration
  const columns: TableColumn<License>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (_, license) => (
        <div className="space-y-1">
          <div className="font-medium">{license.name}</div>
          <div className="text-xs text-muted-foreground font-mono">{license.code}</div>
        </div>
      ),
    },
    {
      key: 'email',
      label: 'Contact',
      sortable: true,
      render: (_, license) => (
        <div className="space-y-1">
          <div className="text-sm">{license.email || '-'}</div>
          <div className="text-xs text-muted-foreground">{license.phone || ''}</div>
        </div>
      ),
    },
    {
      key: 'city',
      label: 'Location',
      sortable: true,
      hideOnTablet: true,
      render: (_, license) => (
        <div className="text-sm">
          {[license.city, license.state, license.country].filter(Boolean).join(', ') || '-'}
        </div>
      ),
    },
    {
      key: 'courierRegistrationNo',
      label: 'Courier Reg. No.',
      sortable: true,
      hideOnTablet: true,
      render: (_, license) => (
        <div className="text-sm font-mono">
          {license.courierRegistrationNo || '-'}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (_, license) => (
        <Badge
          variant={license.status === 'Active' ? 'default' : 'secondary'}
          className={
            license.status === 'Active'
              ? 'bg-green-500 hover:bg-green-600'
              : ''
          }
        >
          {license.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      hideOnTablet: true,
      render: (_, license) => (
        <div className='text-sm text-muted-foreground'>
          {new Date(license.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </div>
      ),
    },
  ];

  // Filter configuration
  const enhancedFilterConfig: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
      ],
      placeholder: 'Filter by status',
    },
  ];

  // Form sections configuration
  const formSections: FormSection[] = [
    {
      title: 'Basic Information',
      description: 'Enter the license name and unique code.',
      fields: [
        {
          name: 'name',
          label: 'License Name',
          type: 'text',
          required: true,
          placeholder: 'Enter license name',
          description: 'A unique name for this license',
          validation: z
            .string()
            .min(2, 'Name must be at least 2 characters')
            .max(200, 'Name cannot exceed 200 characters'),
        },
        {
          name: 'code',
          label: 'License Code',
          type: 'text',
          required: true,
          placeholder: 'Enter license code (e.g., "LIC-001")',
          description: 'A unique code for this license',
          validation: z
            .string()
            .min(2, 'Code must be at least 2 characters')
            .max(50, 'Code cannot exceed 50 characters'),
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: [
            { value: 'Active', label: 'Active - Available for use' },
            { value: 'Inactive', label: 'Inactive - Not available for use' },
          ],
          description: 'Current status of this license',
        },
      ],
    },
    {
      title: 'Contact Information',
      description: 'Enter contact details for this license.',
      fields: [
        {
          name: 'email',
          label: 'Email',
          type: 'text',
          required: false,
          placeholder: 'Enter email address',
          description: 'Email contact for the company',
          validation: z
            .string()
            .email('Please enter a valid email address')
            .max(100, 'Email cannot exceed 100 characters')
            .optional()
            .or(z.literal('')),
        },
        {
          name: 'phone',
          label: 'Phone / Mobile',
          type: 'text',
          required: false,
          placeholder: 'Enter phone number',
          description: 'Phone number of the company',
          validation: z
            .string()
            .max(20, 'Phone number cannot exceed 20 characters')
            .optional(),
        },
        {
          name: 'website',
          label: 'Website',
          type: 'text',
          required: false,
          placeholder: 'Enter website URL',
          description: 'Website of the company',
          validation: z
            .string()
            .max(200, 'Website URL cannot exceed 200 characters')
            .optional(),
        },
      ],
    },
    {
      title: 'Address',
      description: 'Enter the full address for this license.',
      fields: [
        {
          name: 'addressLine1',
          label: 'Address Line 1',
          type: 'text',
          required: false,
          placeholder: 'Enter address line 1',
          description: 'First line of the address',
          validation: z
            .string()
            .max(500, 'Address line 1 cannot exceed 500 characters')
            .optional(),
        },
        {
          name: 'addressLine2',
          label: 'Address Line 2',
          type: 'text',
          required: false,
          placeholder: 'Enter address line 2',
          description: 'Second line of the address',
          validation: z
            .string()
            .max(500, 'Address line 2 cannot exceed 500 characters')
            .optional(),
        },
        {
          name: 'city',
          label: 'City',
          type: 'text',
          required: false,
          placeholder: 'Enter city name',
          description: 'City name',
          validation: z
            .string()
            .max(100, 'City cannot exceed 100 characters')
            .optional(),
        },
        {
          name: 'state',
          label: 'State',
          type: 'text',
          required: false,
          placeholder: 'Enter state name',
          description: 'State name',
          validation: z
            .string()
            .max(100, 'State cannot exceed 100 characters')
            .optional(),
        },
        {
          name: 'country',
          label: 'Country',
          type: 'text',
          required: false,
          placeholder: 'Enter country name',
          description: 'Country name',
          validation: z
            .string()
            .max(100, 'Country cannot exceed 100 characters')
            .optional(),
        },
        {
          name: 'pincode',
          label: 'Pincode',
          type: 'text',
          required: false,
          placeholder: 'Enter pincode',
          description: 'Postal/ZIP code',
          validation: z
            .string()
            .max(20, 'Pincode cannot exceed 20 characters')
            .optional(),
        },
      ],
    },
    {
      title: 'Courier Information',
      description: 'Enter courier registration details.',
      fields: [
        {
          name: 'courierRegistrationNo',
          label: 'Courier Registration No.',
          type: 'text',
          required: false,
          placeholder: 'Enter courier registration number',
          description: 'Courier registration number',
          validation: z
            .string()
            .max(100, 'Courier registration number cannot exceed 100 characters')
            .optional(),
        },
        {
          name: 'authorisedCourierName',
          label: 'Name of the Authorised Courier',
          type: 'text',
          required: false,
          placeholder: 'Enter authorised courier name',
          description: 'Name of the authorised courier',
          validation: z
            .string()
            .max(200, 'Authorised courier name cannot exceed 200 characters')
            .optional(),
        },
        {
          name: 'fileName',
          label: 'File Name',
          type: 'text',
          required: false,
          placeholder: 'Enter file name',
          description: 'Associated file name',
          validation: z
            .string()
            .max(200, 'File name cannot exceed 200 characters')
            .optional(),
        },
        {
          name: 'logo',
          label: 'Logo URL',
          type: 'text',
          required: false,
          placeholder: 'Enter logo URL or file path',
          description: 'URL or path to the logo file (jpg, png, pdf)',
          validation: z
            .string()
            .max(500, 'Logo path cannot exceed 500 characters')
            .optional(),
        },
      ],
    },
  ];

  // Event handlers
  const handleCreate = () => {
    setEditingLicense(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (license: License) => {
    setEditingLicense(license);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = async (license: License) => {
    if (
      window.confirm(
        `Are you sure you want to permanently delete "${license.name}"? This action cannot be undone.`
      )
    ) {
      deleteLicenseMutation.mutate(license._id);
    }
  };

  const handleSubmit = async (data: any) => {
    // Transform the data
    const transformedData = {
      ...data,
      code: data.code.toUpperCase(),
      email: data.email?.toLowerCase() || undefined,
    };

    if (editingLicense) {
      updateLicenseMutation.mutate({
        id: editingLicense._id,
        data: transformedData,
      });
    } else {
      createLicenseMutation.mutate(transformedData);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingLicense(null);
    setServerErrors({});
  };

  const handleRefresh = () => {
    refetch();
    toast({
      title: 'Refreshed',
      description: 'License data has been refreshed',
      variant: 'default',
    });
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();

      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(
        `/api/master-data/license/export?${params.toString()}`
      );

      if (response.data.success) {
        const licenses = response.data.data;

        const exportData = licenses.map((lic: License) => ({
          Name: lic.name,
          Code: lic.code,
          Email: lic.email || '',
          Phone: lic.phone || '',
          'Address Line 1': lic.addressLine1 || '',
          'Address Line 2': lic.addressLine2 || '',
          City: lic.city || '',
          State: lic.state || '',
          Country: lic.country || '',
          Pincode: lic.pincode || '',
          Website: lic.website || '',
          'Courier Reg. No.': lic.courierRegistrationNo || '',
          'Authorised Courier': lic.authorisedCourierName || '',
          'File Name': lic.fileName || '',
          Status: lic.status,
          'Created At': new Date(lic.createdAt).toLocaleString(),
          'Updated At': new Date(lic.updatedAt).toLocaleString(),
        }));

        const headers = Object.keys(exportData[0]);
        const csvContent = [
          headers.join(','),
          ...exportData.map((row: any) =>
            headers
              .map((header) => {
                const value = row[header];
                if (
                  typeof value === 'string' &&
                  (value.includes(',') || value.includes('"'))
                ) {
                  return `"${value.replace(/"/g, '""')}"`;
                }
                return value;
              })
              .join(',')
          ),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `licenses-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast({
          title: 'Success',
          description: `Exported ${licenses.length} licenses`,
          variant: 'default',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Export Failed',
        description:
          error.response?.data?.error?.message || 'Failed to export licenses',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>
            License Management
          </h1>
          <p className='text-muted-foreground'>
            Manage licenses with contact and courier registration information
          </p>
        </div>
      </div>

      <MasterDataTable<License>
        title='Licenses'
        description='Manage and configure license information'
        data={licenses}
        columns={columns}
        isLoading={isLoading}
        serverSide={true}
        pagination={pagination}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        currentSearch={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder='Search licenses by name, code, email, or city...'
        currentFilters={currentFilters}
        onFiltersChange={setCurrentFilters}
        filters={enhancedFilterConfig}
        currentSort={sortConfig}
        onSortChange={setSortConfig}
        onRefresh={handleRefresh}
        onExport={handleExport}
        onAdd={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
        allowImportExport={true}
      />

      <MasterDataForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        title={editingLicense ? 'Edit License' : 'Create License'}
        sections={formSections}
        initialData={editingLicense}
        onSubmit={handleSubmit}
        isLoading={
          createLicenseMutation.isPending || updateLicenseMutation.isPending
        }
        mode={editingLicense ? 'edit' : 'create'}
        serverErrors={serverErrors}
      />
    </div>
  );
};

export default LicenseManagement;

